// docs/114: interactive ground on the client. Every word and every rule is the sim's: the
// interaction table's rows (`matter_book`), the names of surfaces, clouds and things
// (`matter_words`) and which hands a thing answers (`object_verbs`) come from wasm. This file only
// looks them up and says what is on a square.
import { wasmModule } from '../sim/wasm';
import type { InteractVerb, MatterPatch, ReactLook, Snapshot, ThingKind, ThingView, Tile } from '../sim/types';

/** One row of the interaction table (`fk_sim::matter::book::BookRow`). */
export interface BookRow { id: number; on: string; by: string; say: string; look: ReactLook }
interface Word { key: string; name: string; a: boolean; b: boolean }
interface Words { surfaces: Word[]; clouds: Word[]; things: Word[] }

let rows: BookRow[] | undefined;
let words: { surfaces: Map<string, Word>; clouds: Map<string, Word>; things: Map<string, Word> } | undefined;

function load(): void {
  if (rows && words) return;
  try {
    const w = wasmModule();
    rows ??= w.matter_book() as BookRow[];
    if (!words) {
      const ws = w.matter_words() as Words;
      words = {
        surfaces: new Map(ws.surfaces.map((x) => [x.key, x])),
        clouds: new Map(ws.clouds.map((x) => [x.key, x])),
        things: new Map(ws.things.map((x) => [x.key, x])),
      };
    }
  } catch { /* wasm not ready yet: asked again next time */ }
}

/** Every row of the interaction table, in id order. */
export function matterBook(): readonly BookRow[] { load(); return rows ?? []; }

/** Row `id` (0: matter fading on its own — no row). */
export function matterRow(id: number): BookRow | undefined { load(); return id > 0 ? rows?.[id - 1] : undefined; }

/** How row `id` looks as it fires. */
export function reactLook(id: number): ReactLook { return matterRow(id)?.look ?? 'plain'; }

export function surfaceName(s: string): string { load(); return words?.surfaces.get(s)?.name ?? s.toLowerCase(); }
export function cloudName(c: string): string { load(); return words?.clouds.get(c)?.name ?? c.toLowerCase(); }
export function thingName(k: ThingKind): string { load(); return words?.things.get(k)?.name ?? k.toLowerCase(); }
/** Whether a thing stands on its square like a wall (a hanging chandelier does not). */
export function thingSolid(k: ThingKind): boolean { load(); return words?.things.get(k)?.a ?? true; }

const verbCache = new Map<string, InteractVerb[]>();
/** The hands `kind` answers, lit or not (`fk_sim::matter::run::object_verbs`). */
export function thingVerbs(kind: ThingKind, lit: boolean): InteractVerb[] {
  const key = `${kind}:${lit}`;
  let v = verbCache.get(key);
  if (!v) {
    try { v = wasmModule().object_verbs(kind, lit) as InteractVerb[]; } catch { v = []; }
    verbCache.set(key, v);
  }
  return v;
}

/** The thing standing on `t`, if one still does. */
export function thingAt(snap: Pick<Snapshot, 'things'> | undefined, t: Tile): ThingView | undefined {
  return snap?.things?.find((x) => !x.spent && x.tile.x === t.x && x.tile.y === t.y);
}

export function patchAt(snap: Pick<Snapshot, 'board'> | undefined, t: Tile): MatterPatch | undefined {
  return snap?.board.matter?.find((m) => m.tile.x === t.x && m.tile.y === t.y);
}

/**
 * docs/114 MT8: what lies on a square in a few words, as the sim names it — "burning oil",
 * "charged water under steam", "ice". `undefined` for a bare square.
 */
export function describePatch(m: MatterPatch | undefined): string | undefined {
  if (!m) return undefined;
  let s = '';
  if (m.surface && m.fire > 0) s = `burning ${surfaceName(m.surface)}`;
  else if (m.surface && m.charge > 0) s = `charged ${surfaceName(m.surface)}`;
  else if (m.surface) s = surfaceName(m.surface);
  else if (m.fire > 0) s = 'flames';
  if (m.cloud) s = s ? `${s} under ${cloudName(m.cloud)}` : cloudName(m.cloud);
  return s || undefined;
}

/** A thing's words: "barrel of oil", "lit brazier", "burning crate". */
export function describeThing(t: ThingView): string {
  const name = thingName(t.kind);
  if (t.lit && t.burn > 0) return `burning ${name}`;
  return name;
}

/** The hover chip's words for a square's thing and matter together ("barrel of oil", "oil · smoke"). */
export function describeSquare(snap: Pick<Snapshot, 'board' | 'things'> | undefined, t: Tile): string | undefined {
  const thing = thingAt(snap, t);
  const m = describePatch(patchAt(snap, t));
  if (thing && m) return `${describeThing(thing)} on ${m}`;
  return thing ? describeThing(thing) : m;
}

/** Lit things and burning squares, as the lighting layer's extra lights (`LIGHT_LOOKS` order). */
export function matterLights(snap: Pick<Snapshot, 'board' | 'things'>): { tile: Tile; radius: number; look: number }[] {
  const out: { tile: Tile; radius: number; look: number }[] = [];
  const CANDLE: readonly ThingKind[] = ['Candles', 'Chandelier'];
  const TORCH: readonly ThingKind[] = ['Torch', 'Lantern'];
  for (const t of snap.things ?? []) {
    if (t.spent || !t.lit) continue;
    if (CANDLE.includes(t.kind)) out.push({ tile: t.tile, radius: 3, look: 2 });
    else if (TORCH.includes(t.kind)) out.push({ tile: t.tile, radius: 3, look: 0 });
    else out.push({ tile: t.tile, radius: 4, look: 1 });
  }
  for (const m of snap.board.matter ?? []) {
    if (m.fire > 0) out.push({ tile: m.tile, radius: 3, look: 1 });
    else if (m.charge > 0) out.push({ tile: m.tile, radius: 2, look: 4 });
    else if (m.surface === 'Embers') out.push({ tile: m.tile, radius: 2, look: 5 });
  }
  return out;
}
